import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from '@/design-system';

const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Proactively checks for a new service worker version via three strategies:
 *   1. On every route navigation
 *   2. When the tab becomes visible again after being hidden
 *   3. On a periodic 1-hour interval (catches all-day parked sessions)
 *
 * When a new version is found, shows a persistent snackbar prompting the user
 * to reload into it.
 */
export const useServiceWorkerUpdate = () => {
  const showSnackbar = useSnackbar();
  const location = useLocation();

  // Strategy 1: check on every route change
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then(registration => {
      registration.update();
    });
  }, [location]);

  // Strategy 2 & 3: check on tab visibility restore and on a periodic interval
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForUpdate = () => {
      navigator.serviceWorker.ready.then(registration => {
        registration.update();
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkForUpdate);
    const intervalId = setInterval(checkForUpdate, SW_UPDATE_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkForUpdate);
      clearInterval(intervalId);
    };
  }, []);

  // Listen for the custom event dispatched by serviceWorkerRegistration.ts
  // and show a persistent snackbar with an "Update now" CTA.
  useEffect(() => {
    const handleUpdateAvailable = (event: Event) => {
      const registration = (event as CustomEvent<ServiceWorkerRegistration>).detail;

      showSnackbar({
        message: 'A new version is available.',
        buttonLabel: 'Update now',
        hideDismiss: true,
        onClick: () => {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        },
      });
    };

    window.addEventListener('swUpdateAvailable', handleUpdateAvailable);
    return () => window.removeEventListener('swUpdateAvailable', handleUpdateAvailable);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
